import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Mail, Phone, MapPin, Youtube, Twitter, Facebook, Linkedin, Instagram, FaWhatsapp, Send } from 'lucide-react';
import logo from "../assets/images/ABN1.png";

// Helper: Map API types to Icons & Colors
const getSocialIcon = (type) => {
    // Normalize type to lowercase to ensure matching
    const normalizedType = type ? type.toLowerCase() : '';

    switch (normalizedType) {
        case 'facebook':
            return { Icon: Facebook, color: 'bg-[#1877F2] hover:bg-[#145DBF]' };
        case 'linkedin':
            return { Icon: Linkedin, color: 'bg-[#0077B5] hover:bg-[#005E93]' };
        case 'instagram':
            return { Icon: Instagram, color: 'bg-[#E4405F] hover:bg-[#C13584]' };
        case 'twitter':
        case 'tweeter': // Handle potential typo from DB name
            return { Icon: Twitter, color: 'bg-[#1DA1F2] hover:bg-[#0C85D0]' };
        case 'youtube':
            return { Icon: Youtube, color: 'bg-[#FF0000] hover:bg-[#CC0000]' };
        case 'telegram':
            return { Icon: Send, color: 'bg-[#0088CC] hover:bg-[#0077B5]' };
        case 'whatsapp':
            return { Icon: WhatsappIcon, color: 'bg-[#25D366] hover:bg-[#128C7E]' };
        default:
            return { Icon: null, color: 'bg-gray-600' };
    }
};

const WhatsappIcon = ({ className = "w-5 h-5" }) => (
  <svg
    viewBox="0 0 24 24"
    fill="white"
    xmlns="http://www.w3.org/2000/svg"
    className={className}
  >
    <path d="M20.52 3.48A11.94 11.94 0 0012.05 0C5.5 0 .17 5.33.17 11.88c0 2.09.55 4.14 1.6 5.95L0 24l6.36-1.66a11.86 11.86 0 005.69 1.45h.01c6.55 0 11.88-5.33 11.88-11.88 0-3.17-1.23-6.14-3.42-8.43zM12.06 21.7a9.8 9.8 0 01-4.99-1.37l-.36-.21-3.77.99 1-3.68-.23-.38a9.77 9.77 0 01-1.49-5.17c0-5.42 4.41-9.83 9.84-9.83 2.63 0 5.1 1.02 6.96 2.88a9.79 9.79 0 012.88 6.95c0 5.42-4.41 9.82-9.84 9.82zm5.4-7.37c-.29-.15-1.73-.85-2-.95-.27-.1-.47-.15-.67.15-.2.29-.77.95-.95 1.14-.17.2-.35.22-.64.07-.29-.15-1.22-.45-2.32-1.44-.86-.77-1.44-1.72-1.61-2.01-.17-.29-.02-.45.13-.6.13-.13.29-.35.44-.52.15-.17.2-.29.3-.49.1-.2.05-.37-.02-.52-.07-.15-.67-1.62-.92-2.22-.24-.57-.49-.49-.67-.5h-.57c-.2 0-.52.07-.8.37-.27.29-1.05 1.03-1.05 2.52 0 1.49 1.08 2.94 1.23 3.14.15.2 2.12 3.24 5.13 4.55.72.31 1.28.49 1.72.63.72.23 1.37.2 1.88.12.57-.09 1.73-.71 1.98-1.4.25-.69.25-1.28.17-1.4-.08-.12-.27-.2-.56-.35z"/>
  </svg>
);



const Footer = ({ scrollToSection }) => {
    const location = useLocation();
    const navigate = useNavigate();
    const isHomePage = location.pathname === '/';

    // Handle Quick Link clicks - scroll on home, navigate+scroll from other pages
    const handleNavigation = (sectionId) => {
        if (isHomePage && scrollToSection) {
            scrollToSection(sectionId);
        } else {
            navigate('/', { state: { targetSection: sectionId } });
            setTimeout(() => {
                const element = document.getElementById(sectionId);
                if (element) {
                    const headerHeight = 90;
                    const elementPosition = element.getBoundingClientRect().top;
                    const offsetPosition = elementPosition + window.pageYOffset - headerHeight;
                    window.scrollTo({ top: offsetPosition, behavior: 'smooth' });
                }
            }, 100);
        }
    };

    // State for Social Media Icons list
    const [socialData, setSocialData] = useState([]);

    // State for specific Contact Information
    const [contactInfo, setContactInfo] = useState({
        email: null,
        phone: null,
        address: null
    });

    const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

    useEffect(() => {
        const fetchSocialLinks = async () => {
            try {
                // Updated Endpoint based on your request
                const response = await fetch(`${API_BASE_URL}/social-links`);
                const result = await response.json();

                if (result.success && Array.isArray(result.data)) {
                    const allLinks = result.data;

                    // 1. FILTER: Only keep items where isActive is TRUE
                    const activeLinks = allLinks.filter(item => item.isActive);

                    // 2. EXTRACT Contact Details
                    // Note: Your JSON uses the 'link' key for phone numbers too.
                    const emailObj = activeLinks.find(item => item.type === 'email');
                    const phoneObj = activeLinks.find(item => item.type === 'phone');
                    const addressObj = activeLinks.find(item => item.type === 'address');

                    setContactInfo({
                        email: emailObj ? emailObj.link : null,
                        phone: phoneObj ? phoneObj.link : null,
                        address: addressObj ? addressObj.link : null
                    });

                    // 3. EXTRACT Social Media Links
                    // Exclude 'contact' types so they don't appear in the icon list
                    const socialTypes = ['facebook', 'linkedin', 'instagram', 'twitter', 'tweeter', 'youtube', 'telegram', 'whatsapp'];

                    const socials = activeLinks.filter(item =>
                        socialTypes.includes(item.type.toLowerCase())
                    );

                    setSocialData(socials);
                }
            } catch (error) {
                console.error("Error fetching social links:", error);
            }
        };

        fetchSocialLinks();
    }, [API_BASE_URL]);

    return (
        <footer id="site-footer" className="bg-gray-900 text-white py-12 md:py-16 font-sans border-t border-gray-800">
            <div className="container mx-auto px-4 sm:px-6">
                <div className="max-w-7xl mx-auto">

                    {/* Main Grid */}
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-10">

                        {/* Column 1: Logo, Description & Social Icons */}
                        <div className="md:col-span-2 text-center md:text-left">
                            <div className="flex flex-col items-center md:items-start md:flex-row md:space-x-3 mb-6">
                                <img src={logo} alt="ABN Logo" className="w-28 sm:w-32 object-contain mb-4 md:mb-0" />
                                <div>
                                    <div className="text-xl font-bold font-rubik tracking-wide">Ahir Business Network</div>
                                    <div className="text-[#1A87CF] font-medium text-sm">Happy Networking</div>
                                </div>
                            </div>
                            <p className="text-gray-400 mb-6 leading-relaxed text-sm md:text-base max-w-md mx-auto md:mx-0">
                                ABN is Gujarat's premier non-profit business networking organization, connecting business professionals for mutual growth and success.
                            </p>

                            {/* Dynamic Social Icons */}
                            {socialData.length > 0 && (
                                <div className="flex justify-center md:justify-start space-x-3 flex-wrap gap-y-2">
                                    {socialData.map((social) => {
                                        const { Icon, color } = getSocialIcon(social.type);
                                        if (!Icon) return null;

                                        return (
                                            <a
                                                key={social._id}
                                                href={social.type === 'whatsapp' ? `https://wa.me/${social.link}` : social.link}
                                                target="_blank"
                                                rel="noreferrer"
                                                className={`w-10 h-10 rounded-full flex items-center justify-center transition-all duration-300 transform hover:-translate-y-1 ${color}`}
                                                title={social.name}
                                            >
                                                <Icon className="w-5 h-5 text-white" />
                                            </a>
                                        );
                                    })}
                                </div>
                            )}
                        </div>

                        {/* Column 2: Quick Links */}
                        <div className="text-center md:text-left">
                            <h3 className="text-lg font-bold mb-4 md:mb-6 font-rubik text-white border-b border-gray-700 pb-2 inline-block md:block md:border-none">Quick Links</h3>
                            <ul className="space-y-2.5">
                                {['About Us', 'Chapters', 'Activities', 'Members', 'Join ABN'].map((link) => (
                                    <li key={link}>
                                        <button
                                            onClick={() => handleNavigation(link.toLowerCase().split(' ')[0])}
                                            className="text-gray-400 hover:text-[#1A87CF] transition-colors text-sm font-medium hover:translate-x-1 duration-200 inline-block"
                                        >
                                            {link}
                                        </button>
                                    </li>
                                ))}
                                <li>
                                    <Link
                                        to="/privacy-policy"
                                        className="text-gray-400 hover:text-[#1A87CF] transition-colors text-sm font-medium hover:translate-x-1 duration-200 inline-block"
                                    >
                                        Privacy Policy
                                    </Link>
                                </li>
                                <li>
                                    <Link
                                        to="/delete-account"
                                        className="text-red-400 hover:text-red-300 transition-colors text-sm font-medium hover:translate-x-1 duration-200 inline-block"
                                    >
                                        Delete Account
                                    </Link>
                                </li>
                            </ul>
                        </div>

                        {/* Column 3: Contact Info (Strictly Dynamic) */}
                        <div className="text-center md:text-left">
                            <h3 className="text-lg font-bold mb-4 md:mb-6 font-rubik text-white border-b border-gray-700 pb-2 inline-block md:block md:border-none">Contact Us</h3>
                            <div className="space-y-4">

                                {/* Email - Only render if API returned active email */}
                                {contactInfo.email && (
                                    <div className="flex flex-col items-center md:items-start md:flex-row gap-3 group">
                                        <div className="w-8 h-8 rounded-full bg-gray-800 flex items-center justify-center group-hover:bg-[#1A87CF] transition-colors shrink-0">
                                            <Mail size={16} className="text-gray-300 group-hover:text-white" />
                                        </div>
                                        <a href={`mailto:${contactInfo.email}`} className="text-gray-400 text-sm hover:text-white transition-colors break-all mt-1 md:mt-0">
                                            {contactInfo.email}
                                        </a>
                                    </div>
                                )}

                                {/* Phone - Only render if API returned active phone */}
                                {contactInfo.phone && (
                                    <div className="flex flex-col items-center md:items-start md:flex-row gap-3 group">
                                        <div className="w-8 h-8 rounded-full bg-gray-800 flex items-center justify-center group-hover:bg-[#1A87CF] transition-colors shrink-0">
                                            <Phone size={16} className="text-gray-300 group-hover:text-white" />
                                        </div>
                                        <a href={`tel:${contactInfo.phone}`} className="text-gray-400 text-sm hover:text-white transition-colors mt-1 md:mt-0">
                                            {contactInfo.phone}
                                        </a>
                                    </div>
                                )}

                                {/* Address - Only render if API returned active address */}
                                {contactInfo.address && (
                                    <div className="flex flex-col items-center md:items-start md:flex-row gap-3 group">
                                        <div className="w-8 h-8 rounded-full bg-gray-800 flex items-center justify-center group-hover:bg-[#1A87CF] transition-colors shrink-0">
                                            <MapPin size={16} className="text-gray-300 group-hover:text-white" />
                                        </div>
                                        <span className="text-gray-400 text-sm leading-snug mt-1 md:mt-0">
                                            {contactInfo.address}
                                        </span>
                                    </div>
                                )}

                            </div>
                        </div>

                    </div>

                    {/* Bottom Footer */}
                    <div className="border-t border-gray-800 pt-6 mt-2 text-center">
                        <p className="text-gray-500 text-xs sm:text-sm">
                            © {new Date().getFullYear()} Ahir Business Network (ABN). All rights reserved.
                            <span className="block sm:inline sm:ml-2 mt-1 sm:mt-0">Developed by Maqwine Infotech.</span>
                        </p>
                    </div>
                </div>
            </div>
        </footer>
    );
};

export default Footer;